import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';
import { ApiService } from 'src/app/private/services/api.service';
import { environment } from 'src/environments/environment';
import {
  CONTACT_GROUP_LIST_BATCH_SIZE,
  chunkArray,
  ContactGroupBatchProgress,
  ContactGroupOption,
  normalizeContactGroupListResponse,
} from 'src/app/utils/contact-group-batch.util';

export type ContactGroupAssignMode = 'existing' | 'new';

export interface AssignMembersToContactGroupParams {
  mode: ContactGroupAssignMode;
  existingGroupId?: number | string | null;
  groupName?: string;
  group_type?: 'email' | 'whatsapp' | 'call' | 'both' | 'email_call' | 'whatsapp_call' | 'all';
  contacts: any[];
  clientId: number | string;
  centerId?: number | string | null;
  userId?: number | string | null;
  isAdmin?: boolean;
}
@Injectable({
  providedIn: 'root'
})
export class ContactGrouopService {

  constructor(private api: ApiService) { }

  createContact_group(data:any): Observable<any> {
    return this.api.post(environment.createContactGroup,data);
  }

  updateContact_group(data:any): Observable<any> {
    return this.api.post(environment.updateContactGroup,data);
  }

  getLeadsRocord(data:any): Observable<any> {
    return this.api.post(environment.getLeadsRocord,data);
  }

  getAllContactGroups(data:any): Observable<any> {
    return this.api.post(environment.getAllContactGroups,data);
  }

  /** Loads contact groups for dropdown selection (newest first). */
  fetchContactGroupsForSelect(options: {
    clientId: number | string;
    centerId?: number | string | null;
    userId?: number | string;
    isAdmin?: boolean;
  }): Observable<ContactGroupOption[]> {
    const payload: any = {
      where: this.buildContactGroupScopeWhere(options),
      order: {
        col: '"contact_group"."createdAt"',
        dir: 'DESC',
      },
    };

    return this.getAllContactGroups(payload).pipe(
      map((res: any) => normalizeContactGroupListResponse(res))
    );
  }

  private buildContactGroupScopeWhere(options: {
    clientId: number | string;
    centerId?: number | string | null;
    userId?: number | string;
    isAdmin?: boolean;
  }): Record<string, number> {
    const where: Record<string, number> = {
      client_id: parseInt(String(options.clientId), 10),
    };

    const centerId = options.centerId;
    if (centerId != null && centerId !== '' && centerId !== 'null') {
      where['center_id'] = parseInt(String(centerId), 10);
    }

    if (!options.isAdmin && options.userId != null && options.userId !== '') {
      where['user_id'] = parseInt(String(options.userId), 10);
    }

    return where;
  }

  /**
   * Resolves target group (existing or newly created), then adds members in batches.
   */
  assignMembersToContactGroup(
    params: AssignMembersToContactGroupParams
  ): Observable<ContactGroupBatchProgress> {
    const { mode, existingGroupId, groupName, group_type, contacts, clientId, centerId, userId, isAdmin } =
      params;

    if (!contacts?.length) {
      return throwError(() => new Error('No contacts to add.'));
    }

    const createPayload: Record<string, any> = {
      name: String(groupName || '').trim(),
      status: 'Active',
      total_contacts: contacts.length,
      client_id: clientId,
      group_type: group_type || 'both',
    };

    if (centerId != null && centerId !== '' && centerId !== 'null') {
      createPayload['center_id'] = centerId;
    }

    if (!isAdmin && userId != null && userId !== '') {
      createPayload['user_id'] = userId;
    }

    const groupId$ =
      mode === 'existing'
        ? existingGroupId
          ? of(existingGroupId)
          : throwError(() => new Error('Please select a contact group.'))
        : this.createContact_group(createPayload).pipe(
            switchMap((groupRes: any) => {
              if (!groupRes?.id) {
                return throwError(
                  () => new Error('Failed to create contact group.')
                );
              }
              return of(groupRes.id);
            })
          );

    return groupId$.pipe(
      switchMap((groupId) =>
        this.createContactGroupListBatched(groupId, clientId, contacts)
      )
    );
  }

  getAllTemplate(data:any): Observable<any> {
    return this.api.post(environment.getAllWhatsappTemplates,data);
  }

  getAppId(data:any): Observable<any> {
    return this.api.post(environment.getAppId,data);
  }

  createTemplate(data:any): Observable<any> {
    return this.api.post(environment.createWhatsappTemplate,data);
  }


  getAllLanguages(): Observable<any> {
    return this.api.post(environment.getAllLanguages,{});
  }

  updateTemplate(data:any): Observable<any> {
    return this.api.post(environment.updateWhatsappTemplate,data);
  }

  deleteTemplate(data:any): Observable<any> {
    return this.api.post(environment.deleteWhatsappTemplate,data);
  }


  getAllLeads(data:any): Observable<any> {
    return this.api.post(environment.getAllLeads,data);
  }
  
  getAllContacts(data:any): Observable<any> {
    return this.api.post(environment.getAllContactWithCount,data);
  }

  createContactGroupList(data:any): Observable<any> {
    return this.api.post(environment.createContactGroupList,data);
  }

  /**
   * Adds contacts to a group in sequential batches to avoid large payloads/timeouts.
   * Emits progress after each batch; final emission has added === total.
   */
  createContactGroupListBatched(
    contactGroupId: number | string,
    clientId: number | string,
    contacts: any[],
    batchSize: number = CONTACT_GROUP_LIST_BATCH_SIZE
  ): Observable<ContactGroupBatchProgress> {
    const chunks = chunkArray(contacts, batchSize);
    const total = contacts.length;

    if (!chunks.length) {
      return of({ added: 0, total: 0, batchIndex: 0, batchCount: 0 });
    }

    let added = 0;
    return from(chunks).pipe(
      concatMap((batch, batchIndex) =>
        this.createContactGroupList({
          contact_group_id: contactGroupId,
          client_id: clientId,
          contacts: batch,
        }).pipe(
          map(() => {
            added += batch.length;
            return {
              added,
              total,
              batchIndex,
              batchCount: chunks.length,
            };
          })
        )
      )
    );
  }

  getAllContactGroupList(data:any): Observable<any> {
    return this.api.post(environment.getAllContactGroupList,data);
  }

  getCampaignSkipCount(data:any): Observable<any> {
    return this.api.post(environment.getCampaignSkipCount, data);
  }

  deleteGroupListContact(data:any): Observable<any> {
    return this.api.post(environment.deleteContactGroupList,data);
  } 
  addGroupListContact(data:any): Observable<any> {
    return this.api.post(environment.addContactToGroupList,data);
  }
  updateContactGroupList(data:any): Observable<any> {
    return this.api.post(environment.updateContactGroupList,data);
  }

  getLeadAndContactId(data:any): Observable<any> {
    return this.api.post(environment.getLeadAndContactId,data);
  }

  deleteContactGroup(data:any): Observable<any> {
    return this.api.post(environment.deleteContactGroup,data);
  }

  previewWhatsappNumberCheck(data: any): Observable<any> {
    return this.api.post(environment.previewWhatsappNumberCheck, data);
  }

  checkWhatsappNumbers(data: any): Observable<any> {
    return this.api.post(environment.checkWhatsappNumbers, data);
  }

  deleteNonWhatsappContacts(data: any): Observable<any> {
    return this.api.post(environment.deleteNonWhatsappContacts, data);
  }
}


